/** Turning a stretch of maze wall from mountain into forest.
 *
 * `carveLabyrinth` raises every chamber wall as ROCK, because rock is the only
 * kind of wall `repairConnectivity` understands and the connectivity guarantee
 * has to be made before anything else in the pipeline runs. But a map whose
 * every wall is a mountain range is a quarry. Half of what encloses a classic
 * region is woodland, and a wood reads differently: a range says "go round",
 * a wood says "there is something behind this".
 *
 * So the material is applied here instead, at dressing time, and it is applied
 * by UNDOING the rock: the ground goes back to what its neighbours have, and a
 * belt of forest is planted on it. That direction matters. Converting a wall
 * from rock to trees can only ever ADD a way through - if the ground cannot go
 * back, or the tree cannot be planted, the rock simply stays and the cell wears
 * a mountain instead - so nothing here can break the guarantee the rock made.
 *
 * The planting works in RUNS, west to east, for the same reason the ridge
 * dressing does: the wood art is columns cut from one painted belt
 * (`game/tools/range-kit.py`), so consecutive columns reassemble into unbroken
 * canopy with no sky between the crowns. A wall of separate trees would read as
 * an orchard a hero could obviously walk through, which for a wall is the one
 * thing it must not do.
 */

import type { ContentRegistry, TerrainKind } from "@game/core";
import { setTerrain } from "@game/core";
import { plausible } from "./biomes.js";
import { terrainOf } from "./grid.js";
import { groundUnder } from "./maze.js";
import { placeCore } from "./place.js";
import { int } from "./rng.js";
import type { GenState } from "./state.js";

type Payload = Readonly<Record<string, number | string | boolean | null>>;

/** Which belt clothes a wall standing on which ground. */
const BELT: Partial<Record<TerrainKind, readonly string[]>> = {
  grass: ["oak_a", "mixed_a", "pine_a"],
  dirt: ["oak_a", "mixed_a"],
  rough: ["pine_a", "mixed_a"],
  swamp: ["mixed_a"],
  snow: ["pine_snow_a"],
  highlands: ["pine_a", "mixed_a"],
  cavern: ["pine_a"],
};

const nothing = (): Payload => ({});

/**
 * Clothe the wooded walls. Returns how many cells became forest.
 *
 * Runs BEFORE `dressRidges`, which dresses every rock cell it finds: a cell
 * that has already gone back to grass and grown trees is no longer rock, so the
 * two passes divide the walls between them without either knowing about the
 * other.
 */
export function plantWoodWalls(s: GenState, wooded: readonly number[]): number {
  if (wooded.length === 0) return 0;
  const { g } = s;
  const want = new Set(wooded);
  let planted = 0;

  // West to east within a row, so a run of cells gets consecutive columns of one
  // belt and joins seamlessly.
  const rows = new Map<number, number[]>();
  for (const i of want) {
    const y = Math.trunc(i / g.width);
    const row = rows.get(y);
    if (row === undefined) rows.set(y, [i]); else row.push(i);
  }
  for (const row of rows.values()) row.sort((a, b) => a - b);

  for (const y of [...rows.keys()].sort((a, b) => a - b)) {
    const row = rows.get(y)!;
    let at = 0;
    while (at < row.length) {
      let end = at + 1;
      while (end < row.length && row[end] === row[end - 1]! + 1) end++;
      planted += plantRun(s, row.slice(at, end));
      at = end;
    }
  }
  return planted;
}

/**
 * One contiguous west-east stretch of wall, as one stretch of one wood.
 *
 * A run that touches a road's verge is left as rock, whole. The generator keeps
 * one clear row of ground around every road - a road running through unbroken
 * scrub reads as a tunnel - and a wood belt is scrub as far as that rule is
 * concerned, so planting one against a road puts a tree on the verge.
 * Abandoning the WHOLE run rather than the offending cells is deliberate: a
 * belt that stopped for three cells, turned into mountain, and started again
 * would read as a mistake, and rock here is not a loss - it is the same wall
 * wearing a range instead.
 */
function plantRun(s: GenState, cells: readonly number[]): number {
  const { map: m, g } = s;
  if (cells.some((i) => nearRoad(s, i))) return 0;
  // The ground is decided for the run as a whole, from its first cell that has
  // an answer: a belt that changed species halfway along would read as two
  // woods meeting, and the whole point of a run is that it reads as one.
  let ground: TerrainKind | null = null;
  for (const i of cells) {
    ground = groundUnder(m, g, i, plausible);
    if (ground !== null) break;
  }
  if (ground === null) return 0;
  const ids = BELT[ground];
  if (ids === undefined) return 0;
  const belts = ids
    .map((id) => columnsOf(s.content, id))
    .filter((c) => c.length > 0);
  if (belts.length === 0) return 0;

  let planted = 0;
  let at = 0;
  while (at < cells.length) {
    const cols = belts[int(s.r, belts.length)]!;
    const take = Math.min(cells.length - at, cols.length);
    const from = int(s.r, cols.length - take + 1);
    for (let k = 0; k < take; k++) {
      if (plant(s, cells[at + k]!, cols[from + k]!, ground)) planted++;
    }
    at += take;
  }
  return planted;
}

/**
 * Put the ground back and grow one tree on it, or leave the rock alone.
 *
 * The rock is restored on ANY refusal, and that is the whole safety argument:
 * the cell is impassable before this function and impassable after it unless a
 * blocking tree took the rock's place, so the wall never develops a hole this
 * pass did not intend.
 */
function plant(s: GenState, cell: number, defId: string, ground: TerrainKind): boolean {
  const { map: m, g } = s;
  const x = cell % g.width, y = Math.trunc(cell / g.width);
  if (terrainOf(m, cell) !== "rock" || m.occupant[cell] !== 0) return false;

  // Reservations are honoured in full here, unlike in the ridge dressing.
  // There the bypass buys something real - a cell it cannot take is a cell of
  // bare rock showing through the range, a HOLE IN THE WALL. Here it buys
  // nothing: a cell this pass refuses stays rock and wears a mountain, so the
  // wall is unbroken either way and only its material changes. That is not
  // worth widening an invariant the whole generator keeps.
  setTerrain(m, x, y, 0, ground);
  // The cell was impassable rock until the line above, and is impassable again
  // the moment a tree stands on it - or the rock goes back. Either way the
  // walkable set is unchanged, so the connectivity flood has nothing to find.
  if (placeCore(s, defId, x, y, nothing(), { groundWasBlocked: true }) < 0) {
    setTerrain(m, x, y, 0, "rock");
    return false;
  }
  return true;
}

/** Is this cell on a road's verge - the road itself or any of its neighbours? */
function nearRoad(s: GenState, cell: number): boolean {
  const { map: m, g } = s;
  const x = cell % g.width, y = Math.trunc(cell / g.width);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= g.width || ny >= g.height) continue;
      if (m.road[ny * g.width + nx] !== 0) return true;
    }
  }
  return false;
}

/**
 * A belt's columns, counted out of the content set rather than written down -
 * how many a belt has is a fact about its art, and re-authoring it taller
 * re-cuts it into a different number. A stale count would fail silently:
 * `tryPlaceAt` answers null for a def that does not exist exactly as it does
 * for a cell it does not like.
 */
function columnsOf(content: ContentRegistry, id: string): readonly string[] {
  const out: string[] = [];
  for (let k = 0; content.mapObjects.has(`obs_wood_${id}${String(k)}`); k++) {
    out.push(`obs_wood_${id}${String(k)}`);
  }
  return out;
}
